import "./style.scss";

import { Tooltip } from "@mui/material";
import { getValueOrDraft } from "components/Beneficiar/utils";
import { DraftEan13 } from "components/Title";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import { getCleanStartorEndDay } from "helpers/utils";
import { forwardRef } from "react";

import TextSkeleton from "../TextSkeleton";
import TitleCover from "../TitleCover";

interface TitleListItemProps {
    /**
     * Contain informations about the title (object)
     * */
    element?: any;
    /**
     * id of the targeted element it's clicked.
     */
    currentElementId?: number;
    /**
     * index of the title in the array of all titles. It's given to the key props of the title.
     */
    index: number;
    /**
     * Set the current element (setCurrentTitle). It's used to show informations of the selected title on the right panel in the app.
     */
    action?: (element: any) => void;
    /** On edit title or beneficiar disable style for the list */
    disabled?: boolean;
    /** Same row, text values replaced by skeletons. */
    isLoading?: boolean;
}

/**
 * This is 3x component of an item in title list.
 * The following informations about title are displayed :
 * id, avatar, tags color, name, released date, ean13.
 * This component is rendered with .map on the array containing all titles.
 */

const TitleListItem = forwardRef<HTMLDivElement, TitleListItemProps>(function TitleListItem(
    { element, currentElementId, index, action, disabled, isLoading = false }: TitleListItemProps,
    ref,
) {
    return (
        <div
            className="title-list-item"
            style={disabled ? { opacity: 0.1, pointerEvents: "none" } : {}}
            key={`${element?.id ?? "skeleton"}_${index}`}
            ref={ref}
        >
            <div
                key={`${element?.id ?? "skeleton"}_${index}`}
                onClick={isLoading || !action || !element ? undefined : () => action(element)}
            >
                <div
                    className={`titles-list-element${!isLoading && element?.id === currentElementId ? " active" : ""}${
                        isLoading ? " titles-list-element--loading" : ""
                    }`}
                >
                    <div className="avatar-block flex items-end gap-2.5 w-full">
                        <TitleCover
                            title={isLoading ? "" : element.name}
                            coverUrl={isLoading ? undefined : element.avatarUrl}
                            style={{ width: 40, minWidth: 40 }}
                        />
                        <div className="flex flex-col w-full overflow-hidden">
                            <div className="titles-list-element-tags">
                                <div>
                                    {!isLoading &&
                                        element.tags &&
                                        element.tags.slice(0, 3).map((item: any) => (
                                            <Tooltip key={`tooltip-${item.id}`} title={item.name} arrow>
                                                <span
                                                    className={`tags titles-list-element-tag-${item.color}`}
                                                    key={item.id}
                                                ></span>
                                            </Tooltip>
                                        ))}
                                    <span className={`elements-list-element-tag`}></span>
                                </div>
                                <span style={{ fontSize: "12px", color: "#575757" }}>
                                    {isLoading ? (
                                        <TextSkeleton width={`${3 + (index % 3)}em`} />
                                    ) : element.releaseDate && isValid(new Date(element.releaseDate)) ? (
                                        format(getCleanStartorEndDay(element.releaseDate), "d/MM/yyyy")
                                    ) : (
                                        ""
                                    )}
                                </span>
                            </div>
                            <span title={isLoading ? undefined : element.name} className="title-list-title-name">
                                {isLoading ? <TextSkeleton width={`${58 + (index % 4) * 8}%`} /> : element.name}
                            </span>
                            <span style={{ fontSize: "12px", color: "#575757" }} className="title-list-ean13">
                                {isLoading ? (
                                    <TextSkeleton width={`${48 + (index % 3) * 8}%`} />
                                ) : (
                                    getValueOrDraft(element.ean13) || <DraftEan13 />
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default TitleListItem;
